import { useCallback, useMemo, useState } from "react";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  InputSize,
  Show
} from "shared/components";
import {
  Download02Icon,
  FilterIcon,
  PlusSignIcon,
  StarIcon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  CreateLeadDefaults,
  CreateLeadModal
} from "shared/app/LeadsTableView/components/CreateLeadModal";
import { AssigneeFilterPopover } from "shared/components/Assignee";
import { LeadTypeFilterPopover } from "shared/app/LeadsTableView/components/LeadTypeFilterPopover";
import { TagsFilterPopover } from "shared/app/LeadsTableView/components/TagsFilterPopover";
import type { LeadsFilters } from "shared/app/LeadsTableView/types";
import { LeadType } from "views/leads-id/types";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { useLocation } from "react-router-dom";
import { ColumnsMenu } from "shared/app/ApplicationsTableView/components/ColumnsMenu";
import { useMedia } from "shared/providers";
import { ColumnVisibilityItem } from "providers/Auth";
import {
  LEADS_COLUMNS_CONTEXT_LABEL,
  LeadView
} from "shared/app/LeadsTableView/constants";
import { SavedViewsControl } from "shared/app/LeadsTableView/components/SavedViewsControl";
import type { LeadSavedViewsController } from "shared/app/LeadsTableView/components/SavedViewsControl/useLeadSavedViewsController";

type StarredTab = "all" | "starred";
export type LeadsPrimaryTab = "all" | "needs_review";

export type LeadsCsvDownloadState = "ready" | "loading" | "empty" | "exporting";

const NO_LEADS_TO_DOWNLOAD_TOOLTIP = "No leads to download.";

const PIPELINE_DISABLED_TOOLTIP =
  "Select Owner or Tenant leads only to access the Pipeline view";

export const LeadsHeader = ({
  refetchLeads,
  search,
  setSearch,
  isCreateLeadModalOpen,
  hasHeaderTitle,
  setIsCreateLeadModalOpen,
  selectedAssigneeIds,
  setSelectedAssigneeIds,
  setShowFilters,
  createLeadDefaults,
  starredFilter,
  setStarredFilter,
  starredCount,
  columnsVisibility,
  onColumnToggle,
  savedViews,
  view,
  setView,
  isOwnerLeadsEnabled = false,
  selectedLeadTypes,
  onSelectedLeadTypesChange,
  pipelineDisabled = false,
  reviewTabsEnabled = false,
  primaryTab = "all",
  onPrimaryTabChange,
  reviewCount = 0,
  filters,
  onSelectedTagsChange,
  tagsFilterScope,
  downloadCsv
}: {
  refetchLeads: () => void;
  search: string;
  hasHeaderTitle?: boolean;
  setSearch: (search: string) => void;
  isCreateLeadModalOpen: boolean;
  setIsCreateLeadModalOpen: (isOpen: boolean) => void;
  selectedAssigneeIds: number[] | null;
  setSelectedAssigneeIds: (userIds: number[] | null) => void;
  setShowFilters: (show: boolean) => void;
  createLeadDefaults?: CreateLeadDefaults;
  starredFilter: boolean;
  setStarredFilter: (next: boolean) => void;
  starredCount: number;
  columnsVisibility: ColumnVisibilityItem[];
  onColumnToggle: (id: string, checked: boolean) => void;
  savedViews?: LeadSavedViewsController["controlProps"];
  view?: LeadView;
  setView?: (view: LeadView) => void;
  isOwnerLeadsEnabled?: boolean;
  selectedLeadTypes: LeadType[];
  onSelectedLeadTypesChange: (leadTypes: LeadType[]) => void;
  pipelineDisabled?: boolean;
  reviewTabsEnabled?: boolean;
  primaryTab?: LeadsPrimaryTab;
  onPrimaryTabChange?: (tab: LeadsPrimaryTab) => void;
  reviewCount?: number;
  filters?: LeadsFilters;
  onSelectedTagsChange: (tags: { id: string; name: string }[]) => void;
  // What the Tags filter counts over: the embedded table's property / unit scope and
  // the settled (debounced) search.
  tagsFilterScope?: {
    propertyIds?: string[];
    unitIds?: string[];
    search?: string;
  };
  downloadCsv?: { state: LeadsCsvDownloadState; onDownload: () => void };
}) => {
  const [assigneeFilterOpen, setAssigneeFilterOpen] = useState(false);
  const [tagsFilterOpen, setTagsFilterOpen] = useState(false);
  const [typeFilterOpen, setTypeFilterOpen] = useState(false);
  const location = useLocation();
  const { isMobile } = useMedia();

  const viewTabs = useMemo<Tab<LeadView>[]>(
    () => [
      { value: LeadView.List, label: "List view" },
      {
        value: LeadView.Pipeline,
        label: "Pipeline view",
        disabled: pipelineDisabled,
        disabledTooltip: pipelineDisabled
          ? PIPELINE_DISABLED_TOOLTIP
          : undefined
      }
    ],
    [pipelineDisabled]
  );

  const shouldShowSideButtons = !location.pathname.includes("/contacts");

  const starredTabs = useMemo<Tab<StarredTab>[]>(
    () => [
      { value: "all", label: "All" },
      {
        value: "starred",
        label: "Starred",
        leftElement: { type: "icon", component: StarIcon },
        rightElement: { type: "counter", count: starredCount }
      }
    ],
    [starredCount]
  );

  const handleStarredTabChange = useCallback(
    (tab: StarredTab) => setStarredFilter(tab === "starred"),
    [setStarredFilter]
  );

  const primaryTabs = useMemo<Tab<LeadsPrimaryTab>[]>(
    () => [
      { value: "all", label: "All leads" },
      {
        value: "needs_review",
        label: "Needs review",
        rightElement: { type: "counter", count: reviewCount }
      }
    ],
    [reviewCount]
  );

  const showLeadControls = primaryTab === "all";

  return (
    <header>
      <Show if={hasHeaderTitle}>
        <h1 className="text-2xl font-bold text-content-1000 mb-6">Leads</h1>
      </Show>
      <div className="mb-4">
        <div className="space-y-3">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="max-w-[272px] w-full">
              <BaseInput
                id="leads-search"
                size={InputSize.SM}
                hideOptional
                type="search"
                placeholder="Search leads"
                value={search}
                onChange={setSearch}
                className="text-sm font-medium"
              />
            </div>
            <Show if={shouldShowSideButtons}>
              <div className="ml-auto flex flex-wrap-reverse justify-end gap-2">
                {downloadCsv ? (
                  <Button
                    variant={ButtonVariant.BASE}
                    size={ButtonSize.SM}
                    HugeIcon={Download02Icon}
                    loading={downloadCsv.state === "exporting"}
                    loadingMode="keep-label"
                    disabled={downloadCsv.state !== "ready"}
                    tooltip={
                      downloadCsv.state === "empty"
                        ? NO_LEADS_TO_DOWNLOAD_TOOLTIP
                        : undefined
                    }
                    onClick={downloadCsv.onDownload}
                  >
                    {downloadCsv.state === "exporting"
                      ? "Preparing export…"
                      : "Download CSV"}
                  </Button>
                ) : null}
                <Button
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.SM}
                  HugeIcon={PlusSignIcon}
                  onClick={() => setIsCreateLeadModalOpen(true)}
                >
                  Create lead
                </Button>
              </div>
            </Show>
          </div>
          <Show if={reviewTabsEnabled}>
            <Tabs<LeadsPrimaryTab>
              tabs={primaryTabs}
              size="sm"
              fullWidthTabs={false}
              active={primaryTab}
              onChange={(next) => onPrimaryTabChange?.(next)}
              tabStyle="outline"
              className="h-10 w-fit [&_button]:!h-8 [&_button]:!text-xs"
            />
          </Show>
          <Show if={showLeadControls}>
            <div className="flex flex-wrap gap-3 items-center">
              <IconButton
                aria-label="Filters"
                variant={IconButtonVariant.BASE}
                size={IconButtonSize.SM}
                onClick={() => setShowFilters(true)}
                HugeIcon={FilterIcon}
              />
              <ColumnsMenu
                contextLabel={LEADS_COLUMNS_CONTEXT_LABEL}
                contextNoun="table"
                isMobile={isMobile}
                columnsData={columnsVisibility}
                onColumnToggle={onColumnToggle}
                disabled={view === LeadView.Pipeline}
              />
              <AssigneeFilterPopover
                open={assigneeFilterOpen}
                onOpenChange={setAssigneeFilterOpen}
                selectedUserIds={selectedAssigneeIds}
                onSelectedUserIdsChange={setSelectedAssigneeIds}
              />
              <TagsFilterPopover
                open={tagsFilterOpen}
                onOpenChange={setTagsFilterOpen}
                filters={filters}
                onSelectedTagsChange={onSelectedTagsChange}
                propertyIds={tagsFilterScope?.propertyIds}
                unitIds={tagsFilterScope?.unitIds}
                search={tagsFilterScope?.search}
              />
              <Show if={isOwnerLeadsEnabled}>
                <LeadTypeFilterPopover
                  open={typeFilterOpen}
                  onOpenChange={setTypeFilterOpen}
                  selectedLeadTypes={selectedLeadTypes}
                  onSelectedLeadTypesChange={onSelectedLeadTypesChange}
                />
              </Show>
              <Show if={Boolean(setView)}>
                <Tabs<LeadView>
                  tabs={viewTabs}
                  size="sm"
                  fullWidthTabs={false}
                  active={view ?? LeadView.List}
                  onChange={(next) => setView?.(next)}
                  tabStyle="outline"
                  className="h-10 [&_button]:!h-8 [&_button]:!text-xs"
                />
              </Show>
              <Tabs<StarredTab>
                tabs={starredTabs}
                size="sm"
                fullWidthTabs={false}
                active={starredFilter ? "starred" : "all"}
                onChange={handleStarredTabChange}
                tabStyle="outline"
                className="h-10 [&_button]:!h-8 [&_button]:!text-xs [&_button[aria-selected=false]]:text-content-400 [&_button_span.bg-white]:!bg-alpha-100 [&_button_span.bg-white]:!border-transparent [&_button_span.bg-white]:!shadow-none [&_button_svg]:!text-content-200"
              />
              {savedViews ? (
                <div className="ml-auto">
                  <SavedViewsControl {...savedViews} />
                </div>
              ) : null}
            </div>
          </Show>
        </div>
      </div>

      <CreateLeadModal
        open={isCreateLeadModalOpen}
        refetchLeads={refetchLeads}
        onClose={() => setIsCreateLeadModalOpen(false)}
        defaults={createLeadDefaults}
      />
    </header>
  );
};
